<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { hall } from '@/hall'

const emit = defineEmits(['close', 'navigate'])

const isVisible = ref(false)

onMounted(() => {
  isVisible.value = true
})

const handleClose = () => {
  isVisible.value = false
  setTimeout(() => {
    emit('close')
  }, 300) // Match transition duration
}

const userId = ref(hall.userModule.userId || '500497')
const userName = ref(`Guest${userId.value.toString().slice(-4)}`)

const menuItems = [
  { id: 'language', label: 'English', icon: 'globe', value: 'en', hasChevron: true },
  { id: 'download', label: 'Download APP', icon: 'download' },
  { id: 'search', label: 'Search', icon: 'search' },
  { id: 'providers', label: 'Game Providers', icon: 'handshake', hasChevron: true },
  { id: 'events', label: 'Events Center', icon: 'gift', hasChevron: true },
]
</script>

<template>
  <div class="sidebar-wrapper">
    <!-- Backdrop Overlay -->
    <transition name="fade">
      <div v-show="isVisible" class="sidebar-backdrop" @click="handleClose"></div>
    </transition>

    <!-- Sidebar Content -->
    <transition name="slide">
      <div v-show="isVisible" class="sidebar-content">
        <!-- User Profile Header -->
        <header class="user-profile">
          <div class="avatar-wrap">
            <img src="@/assets/hall/usericons/1.webp" class="avatar-img" />
          </div>
          <div class="user-info">
            <div class="name-row">
              <span class="user-name">{{ userName }}</span>
              <div class="vip-badge">
                <img src="@/assets/hall/vip/vip_icon01_lv1.webp" class="vip-icon" />
                <span class="vip-text">VIP0</span>
              </div>
            </div>
            <div class="user-id">ID:{{ userId }}</div>
          </div>
        </header>

        <!-- Quick Action Buttons -->
        <div class="action-buttons">
          <button class="action-btn deposit" @click="emit('navigate', 'Deposit'); emit('close')">
            <svg viewBox="0 0 24 24" class="btn-icon"><path fill="currentColor" d="M21 18H3V6h18v12zm-2-10H5v8h14V8zM7 10h10v4H7v-4z"/></svg>
            Deposit
          </button>
          <button class="action-btn withdraw" @click="emit('navigate', 'Withdraw'); emit('close')">
            <svg viewBox="0 0 24 24" class="btn-icon"><path fill="currentColor" d="M3 6h18v12H3V6zm16 10V8H5v8h14zM7 10h10v4H7v-4z"/></svg>
            Withdraw
          </button>
        </div>

        <!-- Menu Navigation List -->
        <nav class="menu-list">
          <div 
            v-for="item in menuItems" 
            :key="item.id" 
            class="menu-item cursor-pointer"
            @click="emit('navigate', item.id); if(item.id !== 'language') emit('close')"
          >
            <div class="item-left">
              <div class="item-icon-wrap">
                <!-- SVG Icon placeholders -->
                <svg v-if="item.icon === 'globe'" viewBox="0 0 24 24" class="item-icon"><path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93c0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41c0 2.08-.8 3.97-2.1 5.39z"/></svg>
                <svg v-if="item.icon === 'download'" viewBox="0 0 24 24" class="item-icon"><path fill="currentColor" d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7l7-7z"/></svg>
                <svg v-if="item.icon === 'search'" viewBox="0 0 24 24" class="item-icon"><path fill="currentColor" d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5A6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5S14 7.01 14 9.5S11.99 14 9.5 14z"/></svg>
                <svg v-if="item.icon === 'handshake'" viewBox="0 0 24 24" class="item-icon"><path fill="currentColor" d="M12.44 13.91l-1.94-.48l-.7-1.42l-1.42-.7l-.48-1.94l-1.91-.44l-.26-1.93L3.81 6.74L3.18 4.88l-1.86-.63L1 2.39L2.39 1l1.86.32l.63 1.86l1.86.63l.26 1.93l1.91.44l.48 1.94l1.42.7l.7 1.42l1.94.48L13 12.07l1.93-.26l.63-1.86l1.86-.63l.63-1.86l1.86.32L21.31 9l.32 1.86l-1.86.63l-.63 1.86l-1.86.63l-.26 1.93l-1.91.44l-.48 1.94l-1.42.7l-.7 1.42l-1.94-.48l-.44-1.91l-.19-.16zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2zm0 18c-3.31 0-6-2.69-6-6s2.69-6 6-6s6 2.69 6 6s-2.69 6-6 6z"/></svg>
                <svg v-if="item.icon === 'gift'" viewBox="0 0 24 24" class="item-icon"><path fill="currentColor" d="M12.5 1.5l1 1h8v12l-1 1h-8l-1-1v-12zM20 12.5v-8h-6v8h6zm-9-11h-8l-1 1v12l1 1h8l1-1v-12l-1-1zm-1 11h-6v-8h6v8z"/></svg>
              </div>
              <span class="item-label">{{ item.label }}</span>
            </div>
            <div class="item-right">
              <span v-if="item.value" class="item-value">{{ item.label === 'English' ? 'English' : '' }}</span>
              <svg v-if="item.hasChevron" viewBox="0 0 24 24" class="chevron-icon"><path fill="currentColor" d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6l-6 6z"/></svg>
            </div>
          </div>
        </nav>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.sidebar-wrapper {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
}

@media (min-width: 768px) {
  .sidebar-wrapper {
    left: 80px; /* start after pc sidebar */
    top: 56px;  /* start below header */
  }
}

.sidebar-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
}

.sidebar-content {
  width: 250px;
  height: 100%;
  background: var(--theme-nav-bg);
  position: relative;
  box-shadow: 10px 0 30px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  padding: 20px 12px;
}

/* User Profile Header */
.user-profile {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 25px;
}

.avatar-wrap {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.1);
  padding: 2px;
}

.avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.name-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-name {
  color: #fff;
  font-weight: bold;
  font-size: 1rem;
}

.vip-badge {
  display: flex;
  align-items: center;
  background: linear-gradient(to right, #444, #222);
  border: 1px solid #c5924a;
  border-radius: 10px;
  padding: 2px 8px 2px 2px;
  gap: 4px;
  transform: scale(0.9);
}

.vip-icon {
  width: 16px;
  height: 16px;
}

.vip-text {
  color: var(--theme-accent);
  font-size: 0.65rem;
  font-weight: bold;
}

.user-id {
  color: #888;
  font-size: 0.75rem;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 8px;
  margin-bottom: 25px;
}

.action-btn {
  flex: 1;
  height: 40px;
  background: #333;
  border: 1px solid #444;
  border-radius: 6px;
  color: #fff;
  font-size: 0.85rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.action-btn:active {
  background: #444;
}

.btn-icon {
  width: 16px;
  height: 16px;
}

/* Menu List */
.menu-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.menu-item {
  height: 44px;
  background: var(--theme-lobby-bg);
  border: 1px solid var(--theme-border-card);
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 12px;
}

.item-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.item-icon-wrap {
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #888;
}

.item-icon {
  width: 18px;
  height: 18px;
}

.item-label {
  color: #ccc;
  font-size: 0.9rem;
}

.item-right {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #666;
}

.item-value {
  font-size: 0.8rem;
  color: #888;
}

.chevron-icon {
  width: 16px;
  height: 16px;
}

/* Fade Transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Slide Transition */
.slide-enter-active, .slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from, .slide-leave-to {
  transform: translateX(-100%);
}
</style>
